const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
});

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: string | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? dateFormatter.format(date) : fallback;
}

export function formatDateTime(value: string | null | undefined, fallback = '—'): string {
  const date = toDate(value);
  return date ? dateTimeFormatter.format(date) : fallback;
}

/** "3 minutes ago" / "just now" / the given fallback when there is no timestamp. */
export function formatRelative(value: string | null | undefined, fallback = 'Never'): string {
  const date = toDate(value);
  if (!date) return fallback;

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (Math.abs(seconds) < 45) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return relativeFormatter.format(-minutes, 'minute');

  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relativeFormatter.format(-hours, 'hour');

  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return relativeFormatter.format(-days, 'day');

  const months = Math.round(days / 30);
  if (Math.abs(months) < 12) return relativeFormatter.format(-months, 'month');

  return relativeFormatter.format(-Math.round(months / 12), 'year');
}

export function formatCount(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '—';
}

// ─────────────────────────────────────────────────────────────────────────────
// POS-local dates and money strings (tenant portal).
//
// Report dates are the POS's OWN local date strings ("YYYY-MM-DD"); feeding
// them to `new Date(...)` parses them as UTC midnight and can render the
// previous day west of Greenwich. Everything below therefore formats by
// splitting the string — no Date object is ever involved.
//
// Money likewise arrives as a decimal STRING and is displayed digit-for-digit:
// only the thousands separators are inserted, never a rounded float.
// ─────────────────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const POS_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const POS_MONTH = /^(\d{4})-(\d{2})$/;
const POS_TIME = /(\d{2}):(\d{2})(?::\d{2})?/;

function monthName(month: string): string | null {
  const index = Number(month) - 1;
  return MONTH_NAMES[index] ?? null;
}

/** "2026-07-30" → "30 Jul 2026". Accepts a longer timestamp and uses its date part. */
export function formatPosDate(value: string | null | undefined, fallback = '—'): string {
  const match = value ? POS_DATE.exec(value) : null;
  if (!match) return value?.trim() ? value : fallback;
  const name = monthName(match[2]);
  return name === null ? value ?? fallback : `${match[3]} ${name} ${match[1]}`;
}

/** Compact axis/table form: "2026-07-30" → "30 Jul". */
export function formatPosDateShort(value: string | null | undefined, fallback = '—'): string {
  const match = value ? POS_DATE.exec(value) : null;
  if (!match) return fallback;
  const name = monthName(match[2]);
  return name === null ? fallback : `${match[3]} ${name}`;
}

/** "2026-07" → "Jul 2026". */
export function formatPosMonth(value: string | null | undefined, fallback = '—'): string {
  const match = value ? POS_MONTH.exec(value) : null;
  if (!match) return fallback;
  const name = monthName(match[2]);
  return name === null ? fallback : `${name} ${match[1]}`;
}

/** Formats a trend bucket, which is "YYYY-MM-DD" for daily series and "YYYY-MM" for monthly. */
export function formatPosBucket(bucket: string): string {
  return POS_MONTH.test(bucket) ? formatPosMonth(bucket) : formatPosDate(bucket);
}

/** Short form of a trend bucket, for chart axes. */
export function formatPosBucketShort(bucket: string): string {
  return POS_MONTH.test(bucket) ? formatPosMonth(bucket) : formatPosDateShort(bucket);
}

/** "14:30:05" → "14:30". POS times stay in the POS's own clock — no timezone maths. */
export function formatPosTime(value: string | null | undefined, fallback = '—'): string {
  const match = value ? POS_TIME.exec(value) : null;
  return match ? `${match[1]}:${match[2]}` : value?.trim() ? value : fallback;
}

/** A sale's date and its separate time of day, joined so [formatPosDateTime] can show both. */
export function saleDateTime(sale: { date: string; time?: string | null }): string {
  return `${sale.date} ${sale.time ?? ''}`.trim();
}

/** "2026-07-30 14:30:05" → "30 Jul 2026, 14:30" (both parts read from the string). */
export function formatPosDateTime(value: string | null | undefined, fallback = '—'): string {
  if (!value?.trim()) return fallback;
  const date = POS_DATE.exec(value);
  if (!date) return value;
  const time = POS_TIME.exec(value.slice(10));
  const day = formatPosDate(value, fallback);
  return time ? `${day}, ${time[1]}:${time[2]}` : day;
}

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Formats a decimal string exactly as it arrived, with thousands separators and
 * at least two fraction digits. Non-numeric input is passed through untouched
 * rather than silently becoming 0.
 */
export function formatAmount(value: string | null | undefined, fallback = '—'): string {
  const raw = value?.trim();
  if (!raw) return fallback;
  const match = DECIMAL.exec(raw);
  if (!match) return raw;
  const fraction = (match[3] ?? '').padEnd(2, '0');
  return `${match[1]}${groupDigits(match[2])}.${fraction}`;
}

/** Money for display: the tenant's currency plus the untouched figure. */
export function formatMoney(value: string | null | undefined, currency: string, fallback = '—'): string {
  const amount = formatAmount(value, '');
  if (amount === '') return fallback;
  return currency.trim() ? `${currency.trim()} ${amount}` : amount;
}

/**
 * Sign of a decimal string, decided on the characters — never on a float, so a
 * value like "-0.00" is correctly reported as zero.
 */
export function amountSign(value: string | null | undefined): -1 | 0 | 1 {
  const match = value ? DECIMAL.exec(value.trim()) : null;
  if (!match) return 0;
  const nonZero = /[1-9]/.test(`${match[2]}${match[3] ?? ''}`);
  if (!nonZero) return 0;
  return match[1] === '-' ? -1 : 1;
}

/**
 * ONLY for chart geometry and percentage bars, where a float is harmless.
 * Never use the result to render an exact figure.
 */
export function parseAmount(value: string | null | undefined): number {
  const parsed = Number.parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Share of a total, as a rounded whole percent. */
export function formatShare(part: number, total: number): string {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return '—';
  return `${Math.round((part / total) * 100)}%`;
}
